import React, { useEffect, useState, useCallback } from 'react';
import MapEngine from '../components/harvesting/MapEngine';
import { getSpeciesMap } from '../services/aggregateService';

// ─── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  page:    { padding: '28px 32px', maxWidth: 1200, margin: '0 auto' },
  heading: { fontSize: 24, fontWeight: 700, marginBottom: 6 },
  sub:     { color: '#6b7280', fontSize: 14, marginBottom: 24 },
  row:     { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 },
  stat:    {
    background: '#f9fafb', border: '1px solid #e5e7eb',
    borderRadius: 8, padding: '12px 18px', textAlign: 'center', minWidth: 110,
  },
  statNum:  { fontSize: 22, fontWeight: 700, color: '#1d4ed8' },
  statLbl:  { fontSize: 12, color: '#6b7280', marginTop: 2 },
  err:      {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '10px 14px', color: '#991b1b', fontSize: 14, marginBottom: 14,
  },
  info:     {
    background: '#eff6ff', border: '1px solid #bfdbfe',
    borderRadius: 6, padding: '10px 14px', color: '#1e40af', fontSize: 13, marginBottom: 16,
  },
};

const CATEGORY_LABEL = {
  plants_medicine: '🌿 Plants & Medicine',
  hunting:         '🦌 Hunting',
  birds:           '🦅 Birds',
  fishing:         '🎣 Fishing',
};

// Group records by category for MapEngine dataLayers
const toDataLayers = (records) => {
  const groups = {};
  records.forEach((r) => {
    const cat = r.category || 'other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(r);
  });
  return Object.entries(groups).map(([dataTypeId, data]) => ({ dataTypeId, data }));
};

const AggregateSpeciesMapPage = () => {
  const [records,  setRecords]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await getSpeciesMap();
      setRecords(data.records || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load species map data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dataLayers  = toDataLayers(records);
  const communities = new Set(records.map(r => r.tenantId?.name).filter(Boolean)).size;
  const speciesSet  = new Set(records.map(r => r.species).filter(Boolean)).size;

  return (
    <div style={S.page}>
      <h1 style={S.heading}>Species Distribution Map</h1>
      <p style={S.sub}>
        Cross-community view of approved biological harvest records with geographic coordinates.
      </p>

      <div style={S.row}>
        <div style={S.stat}>
          <div style={S.statNum}>{records.length}</div>
          <div style={S.statLbl}>Total Records</div>
        </div>
        <div style={S.stat}>
          <div style={S.statNum}>{communities}</div>
          <div style={S.statLbl}>Communities</div>
        </div>
        <div style={S.stat}>
          <div style={S.statNum}>{speciesSet}</div>
          <div style={S.statLbl}>Species</div>
        </div>
        <div style={S.stat}>
          <div style={S.statNum}>{dataLayers.length}</div>
          <div style={S.statLbl}>Categories</div>
        </div>
      </div>

      <div style={S.info}>
        Categories shown: {Object.values(CATEGORY_LABEL).join(' · ')}. Use the layer toggle in
        the map to show or hide individual categories.
      </div>

      {error && <div style={S.err}>{error}</div>}

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading map data…</p>
      ) : (
        <MapEngine
          dataLayers={dataLayers}
          height="580px"
          onFeatureClick={setSelected}
        />
      )}

      {selected && (
        <div style={{
          marginTop: 16, background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: 8, padding: '14px 18px',
        }}>
          <strong>Selected Record</strong>
          <div style={{ fontSize: 13, marginTop: 8, color: '#374151' }}>
            <div><b>Species:</b> {selected.species || '—'}</div>
            <div><b>Category:</b> {CATEGORY_LABEL[selected.category] || selected.category}</div>
            <div><b>Community:</b> {selected.tenantId?.name || '—'}</div>
            <div><b>Season:</b> {selected.season || '—'}</div>
            <div><b>Traditional Use:</b> {selected.isTraditionalUse ? 'Yes' : 'No'}</div>
            <div><b>Date:</b> {selected.harvestDate ? new Date(selected.harvestDate).toLocaleDateString() : '—'}</div>
          </div>
          <button
            onClick={() => setSelected(null)}
            style={{ marginTop: 10, fontSize: 12, cursor: 'pointer', background: 'none', border: 'none', color: '#6b7280' }}
          >
            ✕ Close
          </button>
        </div>
      )}
    </div>
  );
};

export default AggregateSpeciesMapPage;
